import React, { useEffect, useState, useCallback, useRef } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, ArrowsOutSimple, X, CircleNotch, FilePdf } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import { formatDate } from "@/lib/format";

/**
 * Inline pitch deck preview — Crowdcube-style page-by-page toggle.
 * Renders backend-produced PNG of each PDF page inside a secure blob URL
 * (so the source is never exposed to the browser directly).
 */
export default function PitchDeckPreview({ companyId, companyName }) {
  const [info, setInfo] = useState(null);
  const [page, setPage] = useState(1);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const { user } = useAuth();
  const objectUrlRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/companies/${companyId}/pitch-deck/info`)
      .then((r) => { setInfo(r.data); setPage(1); })
      .catch(() => setInfo({ page_count: 0 }))
      .finally(() => setLoading(false));
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, [companyId]);

  useEffect(() => {
    if (!info || info.page_count === 0) return;
    setLoading(true);
    api.get(`/companies/${companyId}/pitch-deck/pages/${page}`, { responseType: "blob" })
      .then((r) => {
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        const url = URL.createObjectURL(r.data);
        objectUrlRef.current = url;
        setImageUrl(url);
      })
      .catch(() => setImageUrl(null))
      .finally(() => setLoading(false));
  }, [companyId, page, info]);

  const next = useCallback(() => setPage((p) => Math.min(info?.page_count || p, p + 1)), [info]);
  const prev = useCallback(() => setPage((p) => Math.max(1, p - 1)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (!expanded) return;
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "Escape") setExpanded(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [expanded, next, prev]);

  if (!info || info.page_count === 0) {
    return (
      <div className="relative aspect-video rounded-2xl overflow-hidden border border-border bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" data-testid="pitch-deck-empty">
        <div className="absolute inset-0 grid-backdrop opacity-[0.08] pointer-events-none" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <div className="h-14 w-14 rounded-2xl bg-emerald-500/15 ring-1 ring-emerald-400/30 grid place-items-center mb-4">
            <FilePdf size={26} weight="fill" className="text-emerald-400" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-emerald-400/90 mb-1">Pitch Deck</div>
          <div className="font-display font-bold text-lg text-white">Preview coming soon</div>
          <div className="text-xs text-white/60 mt-1.5 max-w-sm">The pitch deck will render page-by-page in this frame with dynamic watermarking once uploaded.</div>
        </div>
      </div>
    );
  }

  const watermark = `${user?.name || ""} · ${user?.email || ""} · ${formatDate(new Date().toISOString())}`;

  const viewer = (isExpanded) => (
    <div
      className={`relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 ${isExpanded ? "h-full" : "aspect-video rounded-2xl"} overflow-hidden group secure-viewer`}
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <div className="absolute inset-0 grid-backdrop opacity-[0.05] pointer-events-none" />
      <div className={`absolute inset-0 flex items-center justify-center ${loading ? "" : "opacity-100"}`}>
        {loading && <CircleNotch size={28} className="text-white/60 animate-spin" />}
      </div>
      {imageUrl && (
        <img
          src={imageUrl}
          alt={`${companyName} pitch deck page ${page}`}
          className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-300 ${loading ? "opacity-0" : "opacity-100"}`}
          draggable={false}
        />
      )}
      {/* Watermark */}
      <div className="watermark-overlay pointer-events-none">
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="watermark-tile">{watermark}</div>
        ))}
      </div>

      {/* Page nav overlays */}
      <button
        onClick={prev}
        disabled={page === 1}
        data-testid="pitch-deck-prev-btn"
        className="absolute left-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/50 backdrop-blur-sm text-white grid place-items-center disabled:opacity-30 hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
        aria-label="Previous page"
      >
        <CaretLeft size={20} weight="bold" />
      </button>
      <button
        onClick={next}
        disabled={page === info.page_count}
        data-testid="pitch-deck-next-btn"
        className="absolute right-3 top-1/2 -translate-y-1/2 h-11 w-11 rounded-full bg-black/50 backdrop-blur-sm text-white grid place-items-center disabled:opacity-30 hover:bg-black/70 transition-all opacity-0 group-hover:opacity-100"
        aria-label="Next page"
      >
        <CaretRight size={20} weight="bold" />
      </button>

      {/* Top-right actions */}
      <div className="absolute top-3 right-3 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        {!isExpanded ? (
          <button
            onClick={() => setExpanded(true)}
            data-testid="pitch-deck-expand-btn"
            className="h-9 px-3 rounded-full bg-black/50 backdrop-blur-sm text-white text-xs font-medium hover:bg-black/70 transition-colors flex items-center gap-1.5"
          >
            <ArrowsOutSimple size={14} weight="bold" /> Full screen
          </button>
        ) : (
          <button
            onClick={() => setExpanded(false)}
            className="h-9 w-9 rounded-full bg-black/50 backdrop-blur-sm text-white grid place-items-center hover:bg-black/70 transition-colors"
          >
            <X size={16} />
          </button>
        )}
      </div>

      {/* Bottom bar */}
      <div className="absolute bottom-0 inset-x-0 h-14 bg-gradient-to-t from-black/70 to-transparent flex items-center justify-between px-5">
        <div className="text-white/90 text-xs">
          <span className="uppercase tracking-[0.15em] font-semibold text-emerald-400">Pitch Deck</span>
          <span className="mx-2 text-white/40">·</span>
          <span className="num-display">Page {page} of {info.page_count}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {Array.from({ length: info.page_count }).map((_, i) => (
            <button
              key={i}
              onClick={() => setPage(i + 1)}
              data-testid={`pitch-deck-dot-${i + 1}`}
              className={`transition-all rounded-full ${
                page === i + 1
                  ? "w-8 h-1.5 bg-emerald-400"
                  : "w-1.5 h-1.5 bg-white/40 hover:bg-white/70"
              }`}
              aria-label={`Go to page ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div data-testid="pitch-deck-preview">{viewer(false)}</div>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[92vh] p-0 gap-0 bg-slate-950 border-slate-800" data-testid="pitch-deck-expanded">
          <DialogTitle className="sr-only">{companyName} Pitch Deck</DialogTitle>
          {viewer(true)}
        </DialogContent>
      </Dialog>
    </>
  );
}
